import {
  AUTO_FEED_ACTUALS_STARTED,
  AUTO_FEED_ACTUALS_PROJECT_STARTED,
  AUTO_FEED_ACTUALS_PROJECT_COMPLETED,
  AUTO_FEED_ACTUALS_PROJECT_FAILED,
  AUTO_FEED_ACTUALS_ALL_COMPLETED,
  AUTO_FEED_ACTUALS_ALL_FAILED,
} from "../types";
import { normalizeWsStatus } from "../ws/wsFrameNormalizer";

function readEtaSeconds(frame) {
  return (
    frame?.etaSecondsRemaining ??
    frame?.estimatedRemainingSeconds ??
    frame?.remainingSeconds ??
    frame?.etaSeconds ??
    null
  );
}

/**
 * Translate a single WsProgressDTO frame from `/topic/auto-feed-actuals/{user}`
 * into a Redux action. Returns `null` for frames we can ignore (unknown
 * statuses, or PARTIAL which we map to ALL_COMPLETED — the selector derives
 * `state === "error"` from failedCount > 0, so no separate PARTIAL action is
 * needed).
 *
 * The BE label format is "Auto-feeding ... (LABOR)" or "(NON_LABOR)" on the
 * STARTED frame; we extract that into the importType so the card copy
 * differentiates between the two.
 */
export function autoFeedActualsFrameToAction(frame) {
  if (!frame || !frame.status) return null;
  switch (normalizeWsStatus(frame.status)) {
    case "STARTED":
      return {
        type: AUTO_FEED_ACTUALS_STARTED,
        payload: {
          total: frame.total ?? 0,
          startedAt: frame.startedAt ?? Date.now(),
          importType: extractImportTypeFromLabel(frame.itemLabel),
          etaSeconds: readEtaSeconds(frame),
        },
      };
    case "PROJECT_STARTED":
      return {
        type: AUTO_FEED_ACTUALS_PROJECT_STARTED,
        payload: {
          index: frame.itemIndex ?? 0,
          label: frame.itemLabel ?? "",
          startedAt: frame.startedAt ?? Date.now(),
          etaSeconds: readEtaSeconds(frame),
        },
      };
    case "COMPLETED":
      return {
        type: AUTO_FEED_ACTUALS_PROJECT_COMPLETED,
        payload: {
          // The only frame of the cascade that names its scenario: the open one is
          // refetched on it (refreshOpenScenarioAfterAutoFeed).
          scenarioId: frame.itemId ?? null,
          index: frame.itemIndex ?? 0,
          label: frame.itemLabel ?? "",
          completedAt: frame.completedAt ?? Date.now(),
          result: frame.result ?? null,
          etaSeconds: readEtaSeconds(frame),
        },
      };
    case "FAILED":
      return {
        type: AUTO_FEED_ACTUALS_PROJECT_FAILED,
        payload: {
          index: frame.itemIndex ?? 0,
          label: frame.itemLabel ?? "",
          completedAt: frame.completedAt ?? Date.now(),
          error: frame.error ?? "Unknown error",
          etaSeconds: readEtaSeconds(frame),
        },
      };
    case "ALL_COMPLETED":
    case "PARTIAL":
      // PARTIAL has the same semantics for the FE — terminal, some failed,
      // some succeeded. The aggregated `state === "error"` derives from
      // failedCount, not the terminal status name.
      return { type: AUTO_FEED_ACTUALS_ALL_COMPLETED, payload: {} };
    case "ALL_FAILED":
      return {
        type: AUTO_FEED_ACTUALS_ALL_FAILED,
        payload: { error: frame.error ?? "All scenarios failed" },
      };
    default:
      return null;
  }
}

function extractImportTypeFromLabel(label) {
  if (!label) return null;
  if (label.includes("LABOR") && !label.includes("NON_LABOR")) return "LABOR";
  if (label.includes("NON_LABOR")) return "NON_LABOR";
  return null;
}
